/**
 * HealthPanel
 *
 * Sección "Salud del número" que vive dentro de cada AccountCard. Muestra:
 *  - Score visual (gauge en gradient verde/amarillo/rojo)
 *  - 5 métricas como progress bars (entrega, lectura, respuesta, bloqueo, uptime)
 *  - Spam reports + uso vs tier diario
 *  - Lista de warnings si los hay
 *  - Throughput actual vs recomendado (avisa si está muy bajo o cerca del límite)
 *
 * Si la cuenta no tiene health (pending_qr), no se renderiza nada — el
 * caller decide qué mostrar en su lugar.
 */

import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Send,
    Eye,
    Reply,
    ShieldAlert,
    Flag,
    Wifi,
    Gauge,
} from "lucide-react";
import type {
    WhatsAppAccountHealth,
    WhatsAppHealthRating,
} from "../types";

interface HealthPanelProps {
    health: WhatsAppAccountHealth;
}

const RATING_META: Record<
    WhatsAppHealthRating,
    { label: string; bg: string; text: string; ring: string; icon: React.ReactNode }
> = {
    green: {
        label: "Excelente",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        ring: "ring-emerald-200",
        icon: <CheckCircle2 size={11} />,
    },
    yellow: {
        label: "En riesgo",
        bg: "bg-amber-50",
        text: "text-amber-700",
        ring: "ring-amber-200",
        icon: <AlertTriangle size={11} />,
    },
    red: {
        label: "Crítico",
        bg: "bg-red-50",
        text: "text-red-700",
        ring: "ring-red-200",
        icon: <ShieldAlert size={11} />,
    },
};

function formatTier(tier: WhatsAppAccountHealth["dailyTier"]): string {
    if (tier === -1) return "Sin límite";
    if (tier >= 1000) return tier.toLocaleString("es-AR");
    return String(tier);
}

export default function HealthPanel({ health }: HealthPanelProps) {
    const meta = RATING_META[health.rating];
    const tierLabel = formatTier(health.dailyTier);
    const usagePercent =
        health.dailyTier > 0
            ? Math.min(100, (health.usedToday / health.dailyTier) * 100)
            : 0;

    return (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
            {/* Header: rating + score */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Activity size={13} className="text-slate-400" />
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Salud del número
                    </h4>
                </div>
                <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text} ring-1 ${meta.ring} flex items-center gap-1`}
                >
                    {meta.icon}
                    {meta.label}
                </span>
            </div>

            {/* Score gauge */}
            <ScoreGauge score={health.score} rating={health.rating} />

            {/* Métricas */}
            <div className="grid grid-cols-1 gap-1.5 mt-3">
                <Metric
                    icon={<Send size={10} />}
                    label="Entrega"
                    value={health.metrics.deliveryRate}
                    unit="%"
                    threshold={{ green: 95, yellow: 85 }}
                />
                <Metric
                    icon={<Eye size={10} />}
                    label="Lectura"
                    value={health.metrics.readRate}
                    unit="%"
                    threshold={{ green: 75, yellow: 50 }}
                />
                <Metric
                    icon={<Reply size={10} />}
                    label="Respuesta"
                    value={health.metrics.replyRate}
                    unit="%"
                    threshold={{ green: 20, yellow: 10 }}
                />
                <Metric
                    icon={<ShieldAlert size={10} />}
                    label="Bloqueo"
                    value={health.metrics.blockRate}
                    unit="%"
                    threshold={{ green: 1, yellow: 2 }}
                    inverse
                />
                <Metric
                    icon={<Wifi size={10} />}
                    label="Uptime"
                    value={health.metrics.uptimePercent}
                    unit="%"
                    threshold={{ green: 95, yellow: 85 }}
                />
            </div>

            {/* Stats secundarios: spam + tier */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <SecondaryStat
                    icon={<Flag size={10} />}
                    label="Reportes spam"
                    value={
                        health.metrics.spamReports === 0
                            ? "Ninguno"
                            : health.metrics.spamReports.toString()
                    }
                    danger={health.metrics.spamReports > 3}
                />
                <SecondaryStat
                    icon={<Gauge size={10} />}
                    label="Uso hoy"
                    value={`${health.usedToday}/${tierLabel}`}
                    sub={
                        usagePercent >= 80
                            ? "Cerca del límite"
                            : usagePercent >= 50
                              ? "Uso medio"
                              : "Margen amplio"
                    }
                    danger={usagePercent >= 80}
                />
            </div>

            {/* Warnings */}
            {health.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 space-y-1">
                    {health.warnings.map((w, i) => (
                        <div
                            key={i}
                            className="text-[11px] text-amber-800 flex items-start gap-1.5 leading-snug"
                        >
                            <AlertTriangle
                                size={11}
                                className="text-amber-600 mt-0.5 flex-shrink-0"
                            />
                            {w}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Sub-components ─── */

function ScoreGauge({
    score,
    rating,
}: {
    score: number;
    rating: WhatsAppHealthRating;
}) {
    const colorClass =
        rating === "green"
            ? "from-emerald-400 to-emerald-500"
            : rating === "yellow"
              ? "from-amber-400 to-amber-500"
              : "from-red-400 to-red-500";

    return (
        <div className="flex items-baseline gap-2">
            <div className="flex-1">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full bg-gradient-to-r ${colorClass} transition-all duration-500`}
                        style={{ width: `${score}%` }}
                    />
                </div>
            </div>
            <div className="text-right min-w-[64px]">
                <span className="text-2xl font-bold text-slate-900 tabular-nums">
                    {score}
                </span>
                <span className="text-[10px] text-slate-400 ml-0.5">/100</span>
            </div>
        </div>
    );
}

interface MetricProps {
    icon: React.ReactNode;
    label: string;
    value: number;
    unit: string;
    /** Thresholds para determinar el color del bar */
    threshold: { green: number; yellow: number };
    /** Si true, valores ALTOS son malos (ej. block rate). Invierte la escala. */
    inverse?: boolean;
}

function Metric({
    icon,
    label,
    value,
    unit,
    threshold,
    inverse = false,
}: MetricProps) {
    const ratingForValue = (): WhatsAppHealthRating => {
        if (inverse) {
            if (value <= threshold.green) return "green";
            if (value <= threshold.yellow) return "yellow";
            return "red";
        }
        if (value >= threshold.green) return "green";
        if (value >= threshold.yellow) return "yellow";
        return "red";
    };
    const r = ratingForValue();
    const colorBar =
        r === "green"
            ? "bg-emerald-400"
            : r === "yellow"
              ? "bg-amber-400"
              : "bg-red-400";
    const colorText =
        r === "green"
            ? "text-emerald-600"
            : r === "yellow"
              ? "text-amber-600"
              : "text-red-600";
    const widthPercent =
        unit === "%" ? Math.min(100, value) : Math.min(100, value);

    return (
        <div className="flex items-center gap-2">
            <span className="text-slate-400 flex-shrink-0">{icon}</span>
            <span className="text-[11px] text-slate-600 flex-shrink-0 w-16">
                {label}
            </span>
            <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full ${colorBar} transition-all`}
                    style={{ width: `${widthPercent}%` }}
                />
            </div>
            <span
                className={`text-[11px] font-bold tabular-nums w-12 text-right ${colorText}`}
            >
                {value.toLocaleString("es-AR", { maximumFractionDigits: 1 })}
                {unit}
            </span>
        </div>
    );
}

function SecondaryStat({
    icon,
    label,
    value,
    sub,
    danger,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    danger?: boolean;
}) {
    return (
        <div className="bg-slate-50 rounded-lg p-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500 font-bold">
                <span className={danger ? "text-red-500" : "text-slate-400"}>
                    {icon}
                </span>
                {label}
            </div>
            <div
                className={`text-xs font-bold mt-0.5 ${
                    danger ? "text-red-700" : "text-slate-900"
                }`}
            >
                {value}
            </div>
            {sub && (
                <div className="text-[9px] text-slate-500 mt-0.5">{sub}</div>
            )}
        </div>
    );
}
